import { useState } from 'react';
import type { PhotoCard, Visibility } from '@photos/shared';
import { useUpdatePhotos } from '../../api/hooks';
import { VISIBILITY_LOOK, VISIBILITY_ORDER, visibilityBlocker, visibilityToast } from '../../lib/visibility';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toasts';
import { Icon } from '../Icon';
import { Modal } from '../Modal';

/** "Who can see this?" for the selected photos (the prototype's `moveDialog`, now for visibility). */
export function VisibilityDialog({ photos, onClose, onDone }: { photos: Pick<PhotoCard, 'id' | 'visibility'>[]; onClose(): void; onDone(): void }) {
  const { access } = useSession();
  const current = [...new Set(photos.map((p) => p.visibility))];
  const [choice, setChoice] = useState<Visibility | null>(current.length === 1 ? current[0]! : null);
  const update = useUpdatePhotos();
  const toast = useToast();
  const n = photos.length;
  const unchanged = choice !== null && current.length === 1 && current[0] === choice;

  const save = () => {
    if (!choice || visibilityBlocker(access, current, choice)) return;
    update.mutate(
      { ids: photos.map((p) => p.id), visibility: choice },
      {
        onSuccess: () => {
          onClose();
          toast(visibilityToast(choice, n));
          onDone();
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t change who can see them', sub: e.message }),
      },
    );
  };

  return (
    <Modal onClose={onClose} labelledBy="vis-title">
      <h3 id="vis-title">Who can see {n === 1 ? 'this photo' : `these ${n} photos`}?</h3>
      <p className="help">
        Visibility decides <b>who can see</b> a photo. Tags only decide how you find it.
      </p>
      {VISIBILITY_ORDER.map((v) => {
        const look = VISIBILITY_LOOK[v];
        const blocked = visibilityBlocker(access, current, v);
        return (
          <label
            key={v}
            className="toggle"
            style={{ display: 'flex', borderRadius: 10, marginBottom: 8, padding: '10px 12px', opacity: blocked ? 0.55 : 1 }}
          >
            <input type="radio" name="vis" value={v} checked={choice === v} disabled={!!blocked} onChange={() => setChoice(v)} />
            <span>
              <b style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: look.color }}>
                <Icon name={look.icon} />
                {look.label}
              </b>
              <br />
              <small style={{ color: 'var(--muted)' }}>{blocked ? `${look.description} · ${blocked}` : look.description}</small>
            </span>
          </label>
        );
      })}
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn primary" id="ok" disabled={update.isPending || !choice || unchanged} onClick={save}>
          Save
        </button>
      </div>
    </Modal>
  );
}
